import { useState } from 'react';
import {
  Page,
  Card,
  DataTable,
  Button,
  Modal,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  Toast,
  Frame,
  BlockStack,
  InlineStack,
  Text,
  Badge,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useShop } from '../hooks/useShop';
import { useCurrency } from '../hooks/useCurrency';
import { client } from '../api/client';
import { Endpoints } from '../api/endpoints';

interface ExpenseRow {
  id: number;
  category: string;
  amount: number | string;
  currency: string;
  description: string | null;
  expense_date: string;
  recurring: boolean;
  frequency: string | null;
}

interface ExpensesResponse {
  data: ExpenseRow[];
}

interface SummaryResponse {
  total: number;
  by_category: { category: string; total: number }[];
}

const CATEGORIES = [
  { label: 'Rent', value: 'rent' },
  { label: 'Salary', value: 'salary' },
  { label: 'Tools', value: 'tools' },
  { label: 'Subscription', value: 'subscription' },
  { label: 'Marketing', value: 'marketing' },
  { label: 'Shipping', value: 'shipping' },
  { label: 'Other', value: 'other' },
];

const today = new Date().toISOString().slice(0, 10);

export default function Expenses() {
  const { shop } = useShop();
  const { formatMoney } = useCurrency(shop?.currency ?? 'USD');

  const list = useApi<ExpensesResponse>(Endpoints.expenses.list);
  const summary = useApi<SummaryResponse>(Endpoints.expenses.summary);

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [category, setCategory] = useState('rent');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [expenseDate, setExpenseDate] = useState(today);
  const [recurring, setRecurring] = useState(false);
  const [frequency, setFrequency] = useState('monthly');

  const refresh = () => {
    list.refetch();
    summary.refetch();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await client.post(Endpoints.expenses.create, {
        category,
        amount: parseFloat(amount || '0'),
        description: description || null,
        expense_date: expenseDate,
        recurring,
        frequency: recurring ? frequency : null,
      });
      setToast('Expense added');
      setModalOpen(false);
      setAmount('');
      setDescription('');
      refresh();
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    await client.delete(Endpoints.expenses.remove(id));
    setToast('Expense deleted');
    refresh();
  };

  const rows =
    list.data?.data?.map((e) => [
      new Date(e.expense_date).toLocaleDateString(),
      e.category,
      e.description ?? '—',
      e.recurring ? <Badge tone="info">{e.frequency ?? 'recurring'}</Badge> : '—',
      formatMoney(e.amount),
      <Button variant="plain" tone="critical" onClick={() => handleDelete(e.id)}>
        Delete
      </Button>,
    ]) ?? [];

  return (
    <Frame>
      <Page
        title="Expenses"
        subtitle="Operating costs beyond per-order fees"
        primaryAction={{ content: 'Add expense', onAction: () => setModalOpen(true) }}
      >
        <BlockStack gap="400">
          <Card>
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingMd">
                Total (last 30 days)
              </Text>
              <Text as="span" variant="headingLg">
                {formatMoney(summary.data?.total ?? 0)}
              </Text>
            </InlineStack>
          </Card>

          <Card>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text']}
              headings={['Date', 'Category', 'Description', 'Recurring', 'Amount', '']}
              rows={rows}
            />
          </Card>
        </BlockStack>
      </Page>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Add expense"
        primaryAction={{ content: 'Save', onAction: handleSave, loading: saving }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setModalOpen(false) }]}
      >
        <Modal.Section>
          <FormLayout>
            <FormLayout.Group>
              <Select label="Category" options={CATEGORIES} value={category} onChange={setCategory} />
              <TextField
                label="Amount"
                type="number"
                prefix="$"
                value={amount}
                onChange={setAmount}
                autoComplete="off"
                requiredIndicator
              />
            </FormLayout.Group>
            <TextField
              label="Description"
              value={description}
              onChange={setDescription}
              autoComplete="off"
            />
            <TextField
              label="Date"
              type="date"
              value={expenseDate}
              onChange={setExpenseDate}
              autoComplete="off"
            />
            <Checkbox label="Recurring expense" checked={recurring} onChange={setRecurring} />
            {recurring && (
              <Select
                label="Frequency"
                options={[
                  { label: 'Weekly', value: 'weekly' },
                  { label: 'Monthly', value: 'monthly' },
                  { label: 'Yearly', value: 'yearly' },
                ]}
                value={frequency}
                onChange={setFrequency}
              />
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>

      {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
    </Frame>
  );
}
